<script lang="ts">
  import {uid} from 'uid';
  import {cn} from 'Catalyst/cn';
  import {onMount, onDestroy} from 'svelte';
  import BaseInput from 'Catalyst/components/BaseInput/BaseInput.svelte';
  import type {Plugin} from 'intl-tel-input';
  import 'intl-tel-input/build/js/utils.js';
  import {phoneInput} from 'Utility/phone/intlTelInput';
  import type {PhoneInputProps} from '.';
  import {phoneInputTvProps} from '.';
  import {getContext} from 'svelte';
  import type {CatalystConfig} from 'Catalyst/components/ThemeContext';
  import type {Writable} from 'svelte/store';
  import {createField} from 'felte';

  // eslint-disable-next-line no-unused-vars
  type $$Props = PhoneInputProps;

  export let id: PhoneInputProps['id'] = uid();
  export let label: PhoneInputProps['label'] = '';
  export let placeholder: PhoneInputProps['placeholder'] = '';
  export let value: PhoneInputProps['value'] = '';
  export let disabled: PhoneInputProps['disabled'] =
    phoneInputTvProps.defaultVariants.disabled;
  export let invalid: PhoneInputProps['invalid'] = false;
  export let autocomplete: PhoneInputProps['autocomplete'] = 'tel';
  export let readonly: PhoneInputProps['readonly'] = false;
  export let initialCountry: PhoneInputProps['initialCountry'] = 'US';
  export let name: PhoneInputProps['name'] = 'phone';
  export let inputTestId: PhoneInputProps['inputTestId'] = `${id}-phone-input`;
  export let labelTestId: PhoneInputProps['labelTestId'] = 'phone-input-label';

  const catalystConfig = getContext<Writable<CatalystConfig>>('catalystConfig');
  export let size: PhoneInputProps['size'] =
    $catalystConfig?.defaultVariants?.size;
  export let style: PhoneInputProps['style'] = $catalystConfig?.defaultVariants
    ?.style as PhoneInputProps['style'];

  let inputElement: HTMLInputElement | null = null;
  let iti: Plugin;
  let countryDialCodeSize: number;

  const initialValue = value;

  const {field, onInput, onBlur} = createField({
    name,
    defaultValue: initialValue,
  });

  const updateCountryDialCodeSize = () => {
    countryDialCodeSize = iti.getSelectedCountryData().dialCode?.length || 1;

    updateValue();
  };

  onMount(() => {
    if (inputElement) {
      iti = phoneInput(inputElement, {
        preferredCountries: ['us', 'ca', 'gb'],
        initialCountry: initialCountry,
        autoPlaceholder: 'off',
        showSelectedDialCode: true,
        fixDropdownWidth: false,
        countrySearch: false,
        useFullscreenPopup: false,
        formatAsYouType: true,
        formatOnDisplay: true,
      });

      updateCountryDialCodeSize();

      inputElement.addEventListener('countrychange', updateCountryDialCodeSize);

      if (initialValue) {
        iti.setNumber(initialValue);
      }
    }
  });

  onDestroy(() => {
    if (inputElement) {
      inputElement.removeEventListener(
        'countrychange',
        updateCountryDialCodeSize
      );
    }
  });

  const updateValue = () => {
    value = iti.getNumber();

    onInput(value);
  };

  $: klass = $$restProps.class;
  $: phoneInputTvSlots = phoneInputTvProps({
    size,
    disabled: !!disabled,
    floating: !!value,
    invalid: !!invalid,
    style,
    showPlaceholder: placeholder && !label,
    oneCharacterCountryDealCode: countryDialCodeSize === 1,
    twoCharacterCountryDealCode: countryDialCodeSize === 2,
    threeCharacterCountryDealCode: countryDialCodeSize === 3,
  });

  $: inputElement?.setCustomValidity(
    invalid ? 'Please enter a valid value.' : ''
  );
</script>

<div class={cn(phoneInputTvSlots.base(), klass)} use:field>
  <BaseInput
    {...$$restProps}
    {id}
    class={phoneInputTvSlots.phoneInputWrapper()}
    {labelTestId}
    {label}
    {disabled}
    {invalid}
    {value}
    {placeholder}
    {style}
    {size}
  >
    <input
      {...$$restProps}
      {id}
      bind:this={inputElement}
      class={phoneInputTvSlots.input()}
      {disabled}
      data-testid={inputTestId}
      {autocomplete}
      {placeholder}
      {readonly}
      type="tel"
      on:input={updateValue}
      on:blur={onBlur}
      slot="input"
    />
  </BaseInput>
</div>
